import { GalleryItemContainer } from "./GalleryItemContainer";
import { ButtonUtils, ObjectBuilder, Image3D, Rectangle3D } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { VideoPreviewPlane } from "./VideoPreviewPlane";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder.js";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { text } from "../../TextResources";
import { StringUtils } from "../utils/StringUtils";
import { DateUtils } from "../../utils/Time";
import { AppView } from "../AppView";
class LabelHelper {
    static createLabel(name, parent, layout, text, mesh) {
        const rootRectangle = new Rectangle3D(`${name}Rectangle`);
        rootRectangle.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        rootRectangle.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        rootRectangle.heightInPixels = mesh ? mesh.m2px(layout.height) : layout.height;
        rootRectangle.topInPixels = mesh ? mesh.m2px(layout.top) : layout.top;
        rootRectangle.leftInPixels = mesh ? mesh.m2px(layout.left) : layout.left;
        rootRectangle.background = layout.background;
        rootRectangle.cornerRadius = mesh ? mesh.m2px(layout.cornerRadius) : layout.cornerRadius;
        rootRectangle.adaptWidthToChildren = true;
        rootRectangle.thickness = 0;
        parent.addControl(rootRectangle);
        const textBlock = ObjectBuilder.createTextBlock3D(mesh, `${name}TextBlock`, text, layout.text);
        rootRectangle.addControl(textBlock);
        return { rootRectangle, textBlock };
    }
}
export class VideoGalleryItemContainer extends GalleryItemContainer {
    get videoClip() { return this._videoClip; }
    constructor(mesh2d, callbacks, onEnterVideoClipCallback) {
        super(mesh2d);
        // private readonly _inactiveBookmarkButton: ImgButton;
        // private readonly _activeBookmarkButton: ImgButton;
        this._itemPurchase = null;
        this._videoClip = null;
        this._videoPreviewPlaneStateChanged = (newState) => {
            const isPlaying = newState === "playing";
            this._imageRectangle.isVisible = !isPlaying;
            if (this._videoPreviewMesh !== null) {
                this._videoPreviewMesh.isVisible = isPlaying;
            }
        };
        this._callbacks = callbacks;
        this.onPointerClickObservable.add(async () => {
            if (this._videoClip !== null) {
                await this._callbacks.getSceneCallbacks().openBroadcastAsync({
                    modelNickname: this._videoClip.modelNickname,
                    videoClip: { id: this._videoClip.id, productId: this._videoClip.product?.productId },
                });
                onEnterVideoClipCallback();
            }
        });
        // Thumbnail of the video-clip:
        {
            const gi = Env.lobby.gallery.galleryItem;
            this._imageRectangle = new Rectangle3D("imageRectangle");
            this._imageRectangle.widthInPixels = this._mesh2D.m2px(gi.image.width);
            this._imageRectangle.heightInPixels = this._mesh2D.m2px(gi.image.height);
            this._imageRectangle.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
            this._imageRectangle.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
            this._imageRectangle.thickness = 0;
            this._imageRectangle.cornerRadius = this._mesh2D.m2px(gi.image.cornerRadius);
            this._itemContainer.addControl(this._imageRectangle);
            this._videoPreviewMesh = MeshBuilder.CreatePlane(`${mesh2d.mesh.name}_videoPreviewMesh`, {
                width: gi.video.width,
                height: gi.video.height,
            }, mesh2d.mesh.getScene());
            this._videoPreviewMesh.parent = mesh2d.mesh;
            this._videoPreviewMesh.renderingGroupId = DcRenderingGroup.galleryItemPreviews;
            this._videoPreviewMesh.position = new Vector3(0 - gi.deltaX, gi.video.previewMeshYOffset, 0);
            this._videoPreviewMesh.isPickable = false;
            this._videoPreviewPlane = new VideoPreviewPlane(this._videoPreviewMesh, {
                width: gi.video.width,
                height: gi.video.height,
                cornerRadius: gi.video.cornerRadius,
            }, this._videoPreviewPlaneStateChanged);
            const stubThumbnail = new Image3D("stubThumbnail", Env.lobby.gallery.defaultPreview.path);
            this._imageRectangle.addControl(stubThumbnail);
            this._picture = new Image3D("button");
            this._imageRectangle.addControl(this._picture);
            const leftTopLabels = new StackPanel("leftTopLabels");
            leftTopLabels.isVertical = false;
            leftTopLabels.horizontalAlignment = VideoGalleryItemContainer.HORIZONTAL_ALIGNMENT_LEFT;
            leftTopLabels.paddingLeftInPixels = this._mesh2D.m2px(gi.video.labels.padding);
            leftTopLabels.paddingTopInPixels = this._mesh2D.m2px(gi.video.labels.padding);
            leftTopLabels.spacing = this._mesh2D.m2px(gi.video.labels.spacing);
            this._itemContainer.addControl(leftTopLabels);
            const purchasedLabel = LabelHelper.createLabel("purchasedLabel", leftTopLabels, gi.video.labels.purchased, text.lobby_gallery_item_purchased, this._mesh2D);
            leftTopLabels.addControl(purchasedLabel.rootRectangle);
            const freeLabel = LabelHelper.createLabel("freeLabel", leftTopLabels, gi.video.labels.free, text.lobby_gallery_item_free, this._mesh2D);
            leftTopLabels.addControl(freeLabel.rootRectangle);
            const rightBottomLabels = new StackPanel("rightBottomLabels");
            rightBottomLabels.isVertical = false;
            rightBottomLabels.horizontalAlignment = VideoGalleryItemContainer.HORIZONTAL_ALIGNMENT_RIGHT;
            rightBottomLabels.verticalAlignment = VideoGalleryItemContainer.VERTICAL_ALIGNMENT_TOP;
            rightBottomLabels.leftInPixels = -this._mesh2D.m2px(gi.hiddenRightPadding);
            rightBottomLabels.topInPixels = this._mesh2D.m2px(gi.image.height - gi.video.labels.duration.height - gi.video.labels.padding);
            rightBottomLabels.paddingRightInPixels = this._mesh2D.m2px(gi.video.labels.padding);
            rightBottomLabels.spacing = this._mesh2D.m2px(gi.video.labels.spacing);
            this._itemContainer.addControl(rightBottomLabels);
            const durationLabel = LabelHelper.createLabel("durationLabel", rightBottomLabels, gi.video.labels.duration, "", this._mesh2D);
            rightBottomLabels.addControl(durationLabel.rootRectangle);
            this._labels = {
                purchased: purchasedLabel,
                free: freeLabel,
                duration: durationLabel,
            };
            this._titleTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2D, "title", "", gi.video.title);
            this._itemContainer.addControl(this._titleTextBlock);
            this._modelNicknameButton = ButtonUtils.CreateImgButton("modelNicknameButton", gi.video.modelNickname.imageLayout, this._mesh2D);
            this._modelNicknameButton.isPointerBlocker = true;
            this._modelNicknameButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
            this._modelNicknameButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
            this._modelNicknameButton.widthInPixels = this._mesh2D.m2px(gi.video.modelNickname.width);
            this._modelNicknameButton.heightInPixels = this._mesh2D.m2px(gi.video.modelNickname.height);
            this._modelNicknameButton.leftInPixels = this._mesh2D.m2px(gi.video.modelNickname.left);
            this._modelNicknameButton.topInPixels = this._mesh2D.m2px(gi.video.modelNickname.top);
            this._modelNicknameButton.onClickObserver.add(() => {
                if (this._videoClip !== null) {
                    const studioModel = {
                        modelId: this._videoClip.modelId,
                        modelNickname: this._videoClip.modelNickname,
                        videoClipCount: 0,
                    };
                    this._callbacks.getSceneCallbacks().setGalleryModel(studioModel);
                }
            });
            this._modelNicknameTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2D, "modelNickname", "", gi.video.modelNickname.textLayout);
            this._modelNicknameButton.addControl(this._modelNicknameTextBlock);
            this._itemContainer.addControl(this._modelNicknameButton);
            // const bookmarkContainer = new Container("bookmarkContainer");
            // bookmarkContainer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
            // bookmarkContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
            // bookmarkContainer.widthInPixels = this._mesh2D.m2px(gi.video.bookmarkButton.width);
            // bookmarkContainer.heightInPixels = this._mesh2D.m2px(gi.video.bookmarkButton.height);
            // bookmarkContainer.leftInPixels = this._mesh2D.m2px(gi.video.bookmarkButton.left);
            // bookmarkContainer.topInPixels = this._mesh2D.m2px(gi.video.bookmarkButton.top);
            // this._itemContainer.addControl(bookmarkContainer);
            //
            // this._inactiveBookmarkButton = ButtonUtils.CreateImgButton("inactiveBookmarkButton", gi.video.bookmarkButton.inactiveBookmarkLayout, this._mesh2D);
            // this._inactiveBookmarkButton.isPointerBlocker = true;
            // bookmarkContainer.addControl(this._inactiveBookmarkButton);
            //
            // this._activeBookmarkButton = ButtonUtils.CreateImgButton("activeBookmarkButton", gi.video.bookmarkButton.activeBookmarkLayout, this._mesh2D);
            // this._activeBookmarkButton.isPointerBlocker = true;
            // bookmarkContainer.addControl(this._activeBookmarkButton);
            //
            // this._inactiveBookmarkButton.onClickObserver.add(async () => {
            //     if (this._videoClip === null || this._callbacks === undefined) return;
            //     try {
            //         await this._callbacks.addBookmarkAsync(this._videoClip.id);
            //         setTimeout(() => {
            //             this._activeBookmarkButton.isVisible = true;
            //             this._inactiveBookmarkButton.isVisible = false;
            //         }, 0);
            //     } catch (e) {
            //         console.error(e);
            //         const message = (e as any).message;
            //         if (message) {
            //             AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.error, message);
            //         }
            //     }
            // });
            // this._activeBookmarkButton.onClickObserver.add(async () => {
            //     if (this._videoClip === null || this._callbacks === undefined) return;
            //     try {
            //         await this._callbacks.removeBookmarkAsync(this._videoClip.id);
            //         setTimeout(() => {
            //             this._inactiveBookmarkButton.isVisible = true;
            //             this._activeBookmarkButton.isVisible = false;
            //         }, 0);
            //     } catch (e) {
            //         console.error(e);
            //         const message = (e as any).message;
            //         if (message) {
            //             AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.error, message);
            //         }
            //     }
            // });
        }
    }
    dispose() {
        super.dispose();
        this._videoPreviewPlane.dispose();
        this._videoPreviewMesh?.dispose();
        this._videoClip = null;
    }
    changeState(state) {
        if (AppView.getInstance().appController.scenesSwitcher.getSitePageParams().broadcastPageParams?.videoClip !== undefined && this._videoPreviewMesh) {
            this._videoPreviewMesh.isVisible = false;
        }
        if (state === "normal") {
            this._videoPreviewPlane.pause();
        }
        else {
            this._videoPreviewPlane.play();
        }
    }
    update(item) {
        this._itemPurchase = item === null
            ? null
            : item.item === undefined ? null : item;
        this._videoClip = this._itemPurchase === null ? item : this._itemPurchase.item;
        const isEmpty = this._videoClip === null;
        this._skeletonContainer.isVisible = isEmpty;
        this._itemContainer.isVisible = !isEmpty;
        this._modelNicknameButton.isEnabled = this._itemPurchase === null;
        if (this._videoClip !== null) {
            // console.log(videoClip.id, videoClip.modelNickname);
            this._videoPreviewPlane.setSourceUrl(this._videoClip.previewVideoUrl);
            if (this._videoClip.coverImageUrl) {
                this._picture.setSource(this._videoClip.coverImageUrl, Env.lobby.gallery.defaultPreview.path);
            }
            else {
                this._picture.setSource(Env.lobby.gallery.defaultPreview.path);
            }
            this._titleTextBlock.text = this._videoClip.title;
            this._modelNicknameTextBlock.text = this._videoClip.modelNickname;
        }
        this._labels.purchased.rootRectangle.isVisible = this._videoClip !== null && this._videoClip.product?.isPurchased === true;
        this._labels.purchased.textBlock.text = this._itemPurchase === null
            ? text.lobby_gallery_item_purchased
            : StringUtils.stringFormat(text.lobby_gallery_item_purchased_with_date, DateUtils.dateToString(this._itemPurchase.purchaseDate).toUpperCase());
        this._labels.free.rootRectangle.isVisible = this._videoClip !== null &&
            (this._videoClip.product?.productPrice === undefined || this._videoClip.product.productPrice === 0);
        this._labels.duration.rootRectangle.isVisible = this._videoClip !== null;
        this._labels.duration.textBlock.text = this._videoClip === null
            ? ""
            : VideoGalleryItemContainer._secondsToTimeText(this._videoClip.duration);
    }
    static _secondsToTimeText(seconds) {
        const h = Math.floor(seconds / 60 / 60);
        const m = Math.floor((seconds - h * 60 * 60) / 60);
        const s = Math.floor(seconds - h * 60 * 60 - m * 60);
        const hh = h === 0 ? "" : `${h}:`;
        const mm = m >= 0 && m < 10 ? `0${m}:` : `${m}:`;
        const ss = s >= 0 && s < 10 ? `0${s}` : `${s}`;
        return `${hh}${mm}${ss}`;
    }
    ;
}
